import * as React from "react";
import {
  AlertCircle,
  Download,
  Mail,
  MailX,
  MoreHorizontal,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  ShieldCheck,
  UserPlus,
  UserX,
  Users2,
  X,
} from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
  toast,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import type {
  StaffDirectoryEntry,
  StaffDirectoryResponse,
  StaffDirectoryRole,
  StaffDirectoryStatus,
  TenantVerticalLiteral,
} from "@eduviaos/shared";
import { InviteTeamMemberSheet } from "@/components/people/InviteTeamMemberSheet";
import { StaffAccessEditorSheet } from "@/components/people/StaffAccessEditorSheet";
import { useTerms } from "@/hooks/useTerms";
import {
  STAFF_ROLE_OPTIONS,
  STAFF_STATUS_OPTIONS,
  useReactivateStaff,
  useResendStaffInvite,
  useRevokeStaffInvite,
  useStaffRoster,
  useSuspendStaff,
} from "@/hooks/useStaff";
import { api } from "@/lib/api";
import { useCsvExport } from "@/hooks/useCsvExport";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";

/**
 * Phase J2 — Staff roster inside `<PeoplePage>`.
 *
 * Read-only surface against the new `GET /me/staff` endpoint (Phase J2
 * BE). Writes (invite / suspend / designation change) stay in their
 * existing surfaces — the first-run wizard's "Invite staff" step and
 * the future Designation admin page. J2 ships the directory; J2.b (a
 * future slice) will add the invite affordance to this toolbar.
 *
 * Filters mirror the BE's contract:
 *   - role (server)
 *   - status (server)
 *   - q-search (server, regex-quoted on the BE for ReDoS safety)
 *
 * Status pill semantics (Iron Rule #16 — `<StatusPill>` only in
 * tabular contexts):
 *   - User `status="active"`         → `active`   (emerald)
 *   - User `status="suspended"`      → `suspended` (red)
 *   - User `status="pending_verification"` AND a pending invite row
 *                                    → `invited`  (amber)
 *   - User `status="pending_verification"` AND an expired invite row
 *                                    → `inactive` (slate, "Invite expired")
 *   - User `status="pending_verification"` AND no invite row
 *                                    → `inactive` (slate, "Pending")
 *
 * The pill collapses the BE's separate `status` and `invite_status`
 * dimensions into a single human-readable cell — admins reading the
 * directory don't care about the separation, only "can this person
 * log in today, and if not, why not".
 */

// Phase E1.f — default pageSize, overridable by the user via the
// shared `<DataTablePagination>` selector (persisted per-user via
// localStorage scope "staff").
const DEFAULT_PAGE_SIZE = 10;

interface PillProjection {
  status: StatusPillStatus;
  label: string;
}

// eslint-disable-next-line react-refresh/only-export-components -- pure helper colocated for Vitest exercise without Fast Refresh impact
export function pillForRow(row: StaffDirectoryEntry): PillProjection {
  if (row.status === "active") {
    return { status: "active", label: "Active" };
  }
  if (row.status === "suspended") {
    return { status: "suspended", label: "Suspended" };
  }
  // pending_verification — disambiguate via invite_status
  if (row.invite_status === "pending") {
    return { status: "invited", label: "Invited" };
  }
  if (row.invite_status === "expired") {
    return { status: "inactive", label: "Invite expired" };
  }
  if (row.invite_status === "revoked") {
    return { status: "inactive", label: "Invite revoked" };
  }
  return { status: "inactive", label: "Pending" };
}

function roleLabel(role: StaffDirectoryRole): string {
  const match = STAFF_ROLE_OPTIONS.find((r) => r.value === role);
  return match ? match.label : role;
}

function formatLastLogin(isoTimestamp: string | null): string {
  if (!isoTimestamp) return "Never";
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Convert an ISO timestamp into a short relative phrase like
 * "in 2 days" or "5 hours ago". Used to give a quick "is this invite
 * still live" signal next to pending/expired rows without consuming a
 * column.
 */
function formatRelative(isoTimestamp: string | null): string | null {
  if (!isoTimestamp) return null;
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) return null;
  const diffMs = parsed.getTime() - Date.now();
  const future = diffMs >= 0;
  const absMs = Math.abs(diffMs);
  const minutes = Math.round(absMs / (60 * 1000));
  const hours = Math.round(absMs / (60 * 60 * 1000));
  const days = Math.round(absMs / (24 * 60 * 60 * 1000));
  let phrase: string;
  if (minutes < 60) {
    phrase = `${minutes}m`;
  } else if (hours < 48) {
    phrase = `${hours}h`;
  } else {
    phrase = `${days}d`;
  }
  return future ? `in ${phrase}` : `${phrase} ago`;
}

/**
 * Phase J2.c — surface the invite send-count + last-sent timestamp as
 * a compact "Sent Nx • last 2h ago" sub-label.
 *
 * Rules:
 * - `null`  → no sub-label (user has no invite row at all)
 * - `0`     → no sub-label (invite exists but has never been emailed —
 *              e.g. a future draft-invite flow; the FE intentionally
 *              stays silent so the row doesn't read as "sent 0x")
 * - `>= 1`  → "Sent Nx • last 2h ago" when a last-sent timestamp
 *              accompanies it, otherwise just "Sent Nx"
 *
 * Returns `null` to signal "render nothing" so the caller can keep
 * the simple `{sub ? <span/> : null}` pattern.
 */
function formatSendCountSubLabel(count: number | null, lastSentAt: string | null): string | null {
  if (count === null || count <= 0) return null;
  const relative = formatRelative(lastSentAt);
  const prefix = `Sent ${count}x`;
  return relative ? `${prefix} • last ${relative}` : prefix;
}

const ROLE_FILTER_OPTIONS: ReadonlyArray<{
  value: "__any__" | StaffDirectoryRole;
  label: string;
}> = [{ value: "__any__", label: "Any role" }, ...STAFF_ROLE_OPTIONS];

const STATUS_FILTER_OPTIONS: ReadonlyArray<{
  value: "__any__" | StaffDirectoryStatus;
  label: string;
}> = [{ value: "__any__", label: "Any status" }, ...STAFF_STATUS_OPTIONS];

export interface StaffTabProps {
  /**
   * Gates the row action menu (Resend / Revoke invite) — true only for
   * users with `manage_students` (matching the J3.b parent-write gate;
   * Phase J2.b reuses the same permission so the FE editor doesn't
   * have to surface a new `manage_staff` toggle that the BE doesn't
   * yet enforce). When false, the invite affordances collapse to
   * nothing for every row.
   */
  canManage?: boolean;
  /**
   * Phase J2.d — separate gate for staff suspend / reactivate row
   * actions. True only for users holding the new `manage_staff`
   * permission (admin-only by default; owner/super_admin via wildcard).
   * Kept distinct from `canManage` so the BE's split between
   * invite-mgmt (still under the wizard's role guard) and HR-mgmt
   * (the new permission) flows cleanly end-to-end.
   */
  canManageStaff?: boolean;
  /** Enables reusable-position assignment and exact per-person access editing. */
  canManageAccess?: boolean;
  /**
   * Phase J2.d — current authenticated user's `id`, used to suppress
   * the suspend / reactivate row actions on the caller's own row. The
   * BE also enforces this (403 ``STAFF_SELF_TARGET_FORBIDDEN``), but
   * hiding the affordance is a friendlier first line of defence.
   * `null` when the auth context hasn't resolved yet — treated the
   * same as "don't render" so a momentarily-unresolved auth doesn't
   * expose the action against the wrong row.
   */
  currentUserId?: string | null;
  /**
   * Phase J4.a — tenant slug used to build the CSV download filename
   * (``staff-<slug>-<YYYY-MM-DD>.csv``). Falls back to ``"tenant"``
   * inside the CSV helper when ``null``/blank so the export still
   * works for momentarily-unresolved auth states.
   */
  tenantSlug?: string | null;
  /** Vertical whose wording the roster uses. Defaults to the signed-in tenant. */
  vertical?: TenantVerticalLiteral;
}

export function StaffTab({
  canManage = false,
  canManageStaff = false,
  canManageAccess = false,
  currentUserId = null,
  tenantSlug = null,
  vertical,
}: StaffTabProps) {
  const { ct, roleLabel: verticalRoleLabel } = useTerms(vertical);
  const displayRole = (role: StaffDirectoryRole) => verticalRoleLabel(role, roleLabel(role));
  const [roleFilter, setRoleFilter] = React.useState<"__any__" | StaffDirectoryRole>("__any__");
  const [statusFilter, setStatusFilter] = React.useState<"__any__" | StaffDirectoryStatus>(
    "__any__",
  );
  const [qInput, setQInput] = React.useState("");
  const [debouncedQ, setDebouncedQ] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "staff",
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  // Phase J2.b — row-action mutation state. Phase E1.b.2 collapsed the
  // page-level `actionBanner` (`role="status"`) + `actionError`
  // (`role="alert"`) state machinery into the global `<Toaster>` —
  // every success/error from Resend / Revoke / Suspend / Reactivate /
  // CSV export now lands on the same Sonner surface (Iron Rule #7
  // single primitive). Auto-dismiss + error copy + error-code branches
  // are preserved verbatim via `friendlyMutationError`.
  const resendMutation = useResendStaffInvite();
  const revokeMutation = useRevokeStaffInvite();
  const [revokeTarget, setRevokeTarget] = React.useState<StaffDirectoryEntry | null>(null);
  const [accessTarget, setAccessTarget] = React.useState<StaffDirectoryEntry | null>(null);
  const [inviteOpen, setInviteOpen] = React.useState(false);

  // Phase J2.d — suspend / reactivate row-action state. Tracks the
  // pending target so the shared <Dialog> can render the correct
  // verb + reason textarea; cleared after success/cancel.
  const suspendMutation = useSuspendStaff();
  const reactivateMutation = useReactivateStaff();
  const [statusChangeTarget, setStatusChangeTarget] = React.useState<{
    row: StaffDirectoryEntry;
    kind: "suspend" | "reactivate";
  } | null>(null);
  const [statusChangeReason, setStatusChangeReason] = React.useState("");

  // Reset the reason textarea every time the dialog target changes so
  // a previous suspend's text doesn't bleed into the next reactivate.
  React.useEffect(() => {
    if (statusChangeTarget === null) return;
    setStatusChangeReason("");
  }, [statusChangeTarget]);

  // Debounce the q-search to avoid one request per keystroke.
  // 220ms matches the parents tab's debounce budget and keeps the
  // request rate well under one per second under typical typing
  // cadence.
  React.useEffect(() => {
    const handle = setTimeout(() => setDebouncedQ(qInput.trim()), 220);
    return () => clearTimeout(handle);
  }, [qInput]);

  const filters = React.useMemo(
    () => ({
      role: roleFilter === "__any__" ? undefined : roleFilter,
      status: statusFilter === "__any__" ? undefined : statusFilter,
      q: debouncedQ.length === 0 ? undefined : debouncedQ,
      limit: pageSize,
      offset: page * pageSize,
    }),
    [roleFilter, statusFilter, debouncedQ, page, pageSize],
  );

  const query = useStaffRoster(filters);
  const { data, isLoading, isFetching, isError, error, refetch } = query;
  const total = data?.total ?? 0;
  const items = React.useMemo(() => data?.items ?? [], [data]);

  const hasFiltersApplied = roleFilter !== "__any__" || statusFilter !== "__any__" || qInput !== "";

  const clearFilters = React.useCallback(() => {
    setRoleFilter("__any__");
    setStatusFilter("__any__");
    setQInput("");
    setPage(0);
  }, []);

  const friendlyMutationError = React.useCallback((err: unknown, fallback: string): string => {
    if (err instanceof ApiError) {
      // 409 INVITE_NOT_PENDING — racey click; the FE gate already
      // tries to prevent this but two admins on the same tenant could
      // collide. Same message in either case.
      if (err.code === "INVITE_NOT_PENDING") {
        return "That invite isn't pending anymore — refresh to see the latest status.";
      }
      // Phase J2.d — server-side guards on the suspend/reactivate
      // surface. We render distinct copy for each so the admin
      // understands what went wrong without reading the JSON.
      const details = (err.details as { code?: string } | undefined) ?? undefined;
      if (details?.code === "STAFF_SELF_TARGET_FORBIDDEN") {
        return "You can't change your own account status. Ask an owner to do it instead.";
      }
      if (details?.code === "STAFF_ALREADY_SUSPENDED") {
        return "That user is already suspended — refresh to see the latest status.";
      }
      if (details?.code === "STAFF_NOT_SUSPENDED") {
        return "That user isn't suspended, so there's nothing to reactivate. Refresh to see the latest status.";
      }
      if (err.code === "NOT_FOUND" || err.status === 404) {
        return "User not found. They may have been removed or moved to another institution.";
      }
      return err.message || fallback;
    }
    if (err instanceof Error && err.message) return err.message;
    return fallback;
  }, []);

  const handleResend = React.useCallback(
    (row: StaffDirectoryEntry) => {
      const inviteId = row.invite_id;
      if (inviteId === null) return;
      resendMutation.mutate(inviteId, {
        onSuccess: () => {
          toast.success(`Invite resent to ${row.email}.`);
        },
        onError: (err) => {
          toast.error(friendlyMutationError(err, "Couldn't resend invite."));
        },
      });
    },
    [resendMutation, friendlyMutationError],
  );

  const confirmRevoke = React.useCallback(() => {
    const target = revokeTarget;
    if (target === null) return;
    const inviteId = target.invite_id;
    if (inviteId === null) return;
    revokeMutation.mutate(inviteId, {
      onSuccess: () => {
        setRevokeTarget(null);
        toast.success(`Invite revoked for ${target.email}.`);
      },
      onError: (err) => {
        toast.error(friendlyMutationError(err, "Couldn't revoke invite."));
      },
    });
  }, [revokeTarget, revokeMutation, friendlyMutationError]);

  // Phase J2.d — confirm the pending suspend/reactivate from the
  // dialog. We branch on `kind` instead of two separate handlers so
  // the dialog can share state + the reason textarea.
  const confirmStatusChange = React.useCallback(() => {
    const target = statusChangeTarget;
    if (target === null) return;
    const reason = statusChangeReason.trim();
    const mutation = target.kind === "suspend" ? suspendMutation : reactivateMutation;
    mutation.mutate(
      { userId: target.row.id, reason: reason || null },
      {
        onSuccess: () => {
          setStatusChangeTarget(null);
          if (target.kind === "suspend") {
            toast.success(`Suspended ${target.row.email}. They can't log in until reactivated.`);
          } else {
            toast.success(`Reactivated ${target.row.email}. They can log in again.`);
          }
        },
        onError: (err) => {
          toast.error(
            friendlyMutationError(
              err,
              target.kind === "suspend"
                ? "Couldn't suspend that user."
                : "Couldn't reactivate that user.",
            ),
          );
        },
      },
    );
  }, [
    statusChangeTarget,
    statusChangeReason,
    suspendMutation,
    reactivateMutation,
    friendlyMutationError,
  ]);

  const statusChangePending = suspendMutation.isPending || reactivateMutation.isPending;

  // Phase J4.a — CSV export of the currently filtered staff roster.
  // Bypasses the TanStack cache + page size and instead iterates the
  // BE in 200-row chunks until the response runs out so a tenant with
  // 4000 staff gets a complete export instead of just the visible
  // page. We honour the same `role` / `status` / `q` filters the user
  // sees in the toolbar so the CSV always matches the on-screen total.
  //
  // Phase D2.f — Migrated to the shared `useCsvExport` primitive,
  // routing errors through the StaffTab-owned `staff-action-error`
  // banner via the hook's `onError` extension.
  //
  // Phase E1.b.2 — that shared `staff-action-error` banner is gone
  // (collapsed onto the global `<Toaster>`), so the same hook callback
  // now lands on `toast.error(...)` with the same `friendlyMutationError`
  // copy. The four mutation surfaces (Resend / Revoke / Suspend /
  // Reactivate) and CSV export still share an error channel — it's
  // just the toast surface now instead of the page banner. The other
  // four CSV pages (Students / Parents / Admissions / AdmissionsTrash)
  // keep their inline `ExportCsvErrorBanner` per the D2.f canonical
  // pattern (those don't share their error channel with mutations, so
  // there's no consolidation pressure on them).
  const csvExport = useCsvExport({
    filenamePrefix: "staff",
    tenantSlug,
    disabled: total === 0 && !isLoading,
    onError: (err) => {
      toast.error(friendlyMutationError(err, ct("Couldn't export the staff roster as CSV.")));
    },
    buildRows: async () => {
      const collected: StaffDirectoryEntry[] = [];
      const CHUNK = 200;
      let offset = 0;
      let expectedTotal: number | null = null;
      // Hard cap at 50k rows to keep memory bounded even if the BE
      // ever returns a runaway total. 50k > any realistic tenant.
      const HARD_CAP = 50_000;
      while (collected.length < HARD_CAP) {
        const search = new URLSearchParams();
        if (roleFilter !== "__any__") search.set("role", roleFilter);
        if (statusFilter !== "__any__") search.set("status", statusFilter);
        if (debouncedQ.length > 0) search.set("q", debouncedQ);
        search.set("limit", String(CHUNK));
        search.set("offset", String(offset));
        const page = await api.get<StaffDirectoryResponse>(`/me/staff?${search.toString()}`);
        collected.push(...page.items);
        if (expectedTotal === null) expectedTotal = page.total;
        offset += page.items.length;
        if (page.items.length < CHUNK) break;
        if (expectedTotal !== null && collected.length >= expectedTotal) break;
      }

      return {
        columns: [
          "Name",
          "Role",
          "Designation",
          "Email",
          "Status",
          "Invite status",
          "Invite send count",
          "Last invite sent",
          "Last login",
        ],
        rows: collected.map((row) => [
          row.full_name,
          displayRole(row.role),
          row.designation ?? "",
          row.email,
          row.status,
          row.invite_status ?? "",
          row.invite_email_send_count ?? "",
          row.invite_last_email_sent_at ?? "",
          row.last_login_at ?? "",
        ]),
      };
    },
  });

  const exportDisabled = csvExport.isExporting || (total === 0 && !isLoading);

  return (
    <div data-testid="people-staff-tab">
      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mb-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            {ct("Couldn't load staff roster")}
            {error instanceof Error && error.message ? `: ${error.message}` : "."}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable responsive="cards">
        <DataTableToolbar>
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => {
                setQInput(e.target.value);
                setPage(0);
              }}
              placeholder="Search name or email…"
              className="pl-8"
              aria-label={ct("Search staff")}
            />
          </div>
          <Select
            value={roleFilter}
            onValueChange={(v) => {
              setRoleFilter(v as typeof roleFilter);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-9 w-32" aria-label="Role filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLE_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.value === "__any__" ? opt.label : displayRole(opt.value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as typeof statusFilter);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-9 w-44" aria-label="Status filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasFiltersApplied ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          {canManageStaff ? (
            <Button size="sm" onClick={() => setInviteOpen(true)} data-testid="staff-invite-open">
              <UserPlus aria-hidden="true" className="size-4" />
              {ct("Invite a staff member")}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={csvExport.handleExport}
            disabled={exportDisabled}
            aria-label={ct("Download staff roster as CSV")}
            data-testid="staff-export-csv"
          >
            <Download
              aria-hidden="true"
              className={cn("size-4", csvExport.isExporting && "animate-pulse")}
            />
            {csvExport.isExporting ? "Exporting…" : "Download CSV"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw aria-hidden="true" className={cn("size-4", isFetching && "animate-spin")} />
            Refresh
          </Button>
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[22%]">Name</TableHead>
                <TableHead className="w-[10%]">Role</TableHead>
                <TableHead className="w-[18%]">Designation</TableHead>
                <TableHead className="w-[20%]">Email</TableHead>
                <TableHead className="w-[12%]">Last login</TableHead>
                <TableHead className="w-[14%]">Status</TableHead>
                <TableHead className="w-[4%] text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={7} />
            ) : (
              <TableBody>
                {items.map((row) => {
                  const pill = pillForRow(row);
                  const inviteSubLabel =
                    row.invite_status === "pending" && row.invite_expires_at
                      ? `Expires ${formatRelative(row.invite_expires_at) ?? "soon"}`
                      : row.invite_status === "expired" && row.invite_expires_at
                        ? `Expired ${formatRelative(row.invite_expires_at) ?? "recently"}`
                        : null;
                  const sendCountSubLabel = formatSendCountSubLabel(
                    row.invite_email_send_count,
                    row.invite_last_email_sent_at,
                  );
                  const canResend =
                    canManage &&
                    row.invite_id !== null &&
                    (row.invite_status === "pending" || row.invite_status === "expired");
                  const canRevoke =
                    canManage && row.invite_id !== null && row.invite_status === "pending";
                  // Phase J2.d — suspend / reactivate gates. We hide
                  // the row's own affordance for the current user to
                  // mirror the BE's self-target guard; we also gate
                  // pending_verification rows out — those represent
                  // un-redeemed invites with no live password to lock
                  // and are handled via the revoke-invite flow.
                  const isSelf = currentUserId !== null && row.id === currentUserId;
                  const canSuspend = canManageStaff && !isSelf && row.status === "active";
                  const canReactivate = canManageStaff && !isSelf && row.status === "suspended";
                  const canEditAccess =
                    canManageAccess &&
                    !isSelf &&
                    row.status !== "pending_verification" &&
                    row.role !== "owner";
                  const hasActions =
                    canEditAccess || canResend || canRevoke || canSuspend || canReactivate;
                  return (
                    <TableRow key={row.id} data-testid={`staff-row-${row.id}`}>
                      <TableCell className="font-medium">
                        <div className="truncate">{row.full_name ?? "(no name)"}</div>
                        {row.branch_id ? (
                          <div className="text-muted-foreground truncate text-xs">
                            {row.branch_id}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {displayRole(row.role)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        <div className="truncate">{row.designation ?? "—"}</div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        <div className="flex items-center gap-1.5 truncate">
                          <Mail aria-hidden="true" className="size-3 shrink-0" />
                          {row.email}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs tabular-nums">
                        {formatLastLogin(row.last_login_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <StatusPill status={pill.status} label={pill.label} />
                          {inviteSubLabel ? (
                            <span className="text-muted-foreground text-[10px] tabular-nums">
                              {inviteSubLabel}
                            </span>
                          ) : null}
                          {sendCountSubLabel ? (
                            <span
                              className="text-muted-foreground text-[10px] tabular-nums"
                              data-testid={`staff-row-send-count-${row.id}`}
                            >
                              {sendCountSubLabel}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {hasActions ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Actions for ${row.full_name ?? row.email}`}
                                data-testid={`staff-row-actions-${row.id}`}
                                className="size-7"
                              >
                                <MoreHorizontal aria-hidden="true" className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {canEditAccess ? (
                                <DropdownMenuItem
                                  onSelect={() => setAccessTarget(row)}
                                  data-testid={`staff-action-access-${row.id}`}
                                >
                                  <ShieldCheck aria-hidden="true" className="size-4" />
                                  Edit access
                                </DropdownMenuItem>
                              ) : null}
                              {canResend ? (
                                <DropdownMenuItem
                                  onSelect={() => handleResend(row)}
                                  disabled={resendMutation.isPending}
                                  data-testid={`staff-action-resend-${row.id}`}
                                >
                                  <Send aria-hidden="true" className="size-4" />
                                  {row.invite_status === "expired"
                                    ? "Resend (new link)"
                                    : "Resend invite"}
                                </DropdownMenuItem>
                              ) : null}
                              {canRevoke ? (
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive focus:bg-destructive/10"
                                  onSelect={() => setRevokeTarget(row)}
                                  data-testid={`staff-action-revoke-${row.id}`}
                                >
                                  <MailX aria-hidden="true" className="size-4" />
                                  Revoke invite
                                </DropdownMenuItem>
                              ) : null}
                              {canSuspend ? (
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive focus:bg-destructive/10"
                                  onSelect={() => setStatusChangeTarget({ row, kind: "suspend" })}
                                  data-testid={`staff-action-suspend-${row.id}`}
                                >
                                  <UserX aria-hidden="true" className="size-4" />
                                  Suspend user
                                </DropdownMenuItem>
                              ) : null}
                              {canReactivate ? (
                                <DropdownMenuItem
                                  onSelect={() =>
                                    setStatusChangeTarget({ row, kind: "reactivate" })
                                  }
                                  data-testid={`staff-action-reactivate-${row.id}`}
                                >
                                  <RotateCcw aria-hidden="true" className="size-4" />
                                  Reactivate user
                                </DropdownMenuItem>
                              ) : null}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <span className="text-muted-foreground text-xs" aria-hidden="true">
                            —
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<Users2 aria-hidden="true" />}
            title={hasFiltersApplied ? "No matches" : ct("No staff yet")}
            description={
              hasFiltersApplied
                ? ct("Adjust filters or clear them to see every staff member.")
                : ct(
                    "Invite admins, teachers, and staff from the “Invite staff” step. They show up here once accepted, and as “Invited” while their invite is still live.",
                  )
            }
            action={
              hasFiltersApplied ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filters
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && items.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              Showing {items.length.toLocaleString()} of {total.toLocaleString()}
            </span>
            <DataTablePagination
              className="ml-auto"
              page={page + 1}
              pageSize={pageSize}
              totalRows={total}
              isFetching={isFetching}
              onPageChange={(next) => setPage(Math.max(0, next - 1))}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(0);
              }}
              testIdPrefix="staff-pagination"
            />
          </DataTableFooter>
        ) : null}
      </DataTable>

      <Dialog
        open={revokeTarget !== null}
        onOpenChange={(open) => {
          if (!open && !revokeMutation.isPending) {
            setRevokeTarget(null);
          }
        }}
      >
        <DialogContent data-testid="staff-revoke-dialog">
          <DialogHeader>
            <DialogTitle>Revoke invite?</DialogTitle>
            <DialogDescription>
              {revokeTarget ? (
                <>
                  This will invalidate the pending invite link for{" "}
                  <span className="text-foreground font-medium">{revokeTarget.email}</span>. They
                  won't be able to accept it anymore. You can always send a new invite from the
                  first-run wizard.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRevokeTarget(null)}
              disabled={revokeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRevoke}
              disabled={revokeMutation.isPending}
              data-testid="staff-revoke-confirm"
            >
              {revokeMutation.isPending ? "Revoking…" : "Revoke invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InviteTeamMemberSheet open={inviteOpen} onOpenChange={setInviteOpen} />

      <StaffAccessEditorSheet
        open={accessTarget !== null}
        onOpenChange={(open) => {
          if (!open) setAccessTarget(null);
        }}
        staff={accessTarget}
      />

      <Dialog
        open={statusChangeTarget !== null}
        onOpenChange={(open) => {
          if (!open && !statusChangePending) {
            setStatusChangeTarget(null);
          }
        }}
      >
        <DialogContent data-testid="staff-status-change-dialog">
          <DialogHeader>
            <DialogTitle>
              {statusChangeTarget?.kind === "suspend" ? "Suspend user?" : "Reactivate user?"}
            </DialogTitle>
            <DialogDescription>
              {statusChangeTarget ? (
                statusChangeTarget.kind === "suspend" ? (
                  <>
                    This will block sign-in for{" "}
                    <span className="text-foreground font-medium">
                      {statusChangeTarget.row.email}
                    </span>{" "}
                    until you reactivate them. Existing sessions remain valid until they expire; new
                    sign-in attempts are refused. You can reverse this at any time.
                  </>
                ) : (
                  <>
                    This will restore sign-in access for{" "}
                    <span className="text-foreground font-medium">
                      {statusChangeTarget.row.email}
                    </span>
                    . They'll be able to log in immediately.
                  </>
                )
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="staff-status-change-reason" className="text-xs font-medium">
              Reason{" "}
              <span className="text-muted-foreground font-normal">(optional, audit trail)</span>
            </Label>
            <Textarea
              id="staff-status-change-reason"
              value={statusChangeReason}
              onChange={(e) => setStatusChangeReason(e.target.value)}
              placeholder={
                statusChangeTarget?.kind === "suspend"
                  ? "Left the team, account compromise, ..."
                  : "Back on the team, situation resolved, ..."
              }
              maxLength={280}
              rows={2}
              data-testid="staff-status-change-reason"
              disabled={statusChangePending}
            />
            <p className="text-muted-foreground text-[10px] tabular-nums">
              {statusChangeReason.length}/280
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setStatusChangeTarget(null)}
              disabled={statusChangePending}
            >
              Cancel
            </Button>
            <Button
              variant={statusChangeTarget?.kind === "suspend" ? "destructive" : "default"}
              onClick={confirmStatusChange}
              disabled={statusChangePending}
              data-testid="staff-status-change-confirm"
            >
              {statusChangePending
                ? statusChangeTarget?.kind === "suspend"
                  ? "Suspending…"
                  : "Reactivating…"
                : statusChangeTarget?.kind === "suspend"
                  ? "Suspend user"
                  : "Reactivate user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
